// PricingDetails/PricingDetails.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DynamicPricingForm from './DynamicPricingForm/DynamicPricingForm';
import { getPricingFields } from '../../../../../helpers/PricingFields/pricingFields';
import { getProductTypeFromCategory } from '../../../../../helpers/productTypeMapping';
import { fetchProductDetails } from '../../../../../utils/api-service/get-services';
import { savePricingDetails } from '../../../../../utils/api-service/post-services';

const PricingDetails = ({ selectedCategory, productId: propProductId }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [productType, setProductType] = useState("");
    const [fields, setFields] = useState([]);
    const [formData, setFormData] = useState({});
    const [product, setProduct] = useState(null);
    const [initialized, setInitialized] = useState(false);

    // Use either prop productId or URL param
    const effectiveProductId = propProductId || id;

    // Determine product type from selected category
    useEffect(() => {
        if (selectedCategory) {
            setProductType(selectedCategory?.productType);
        }
    }, [selectedCategory]);

    // Load fields when productType changes
    useEffect(() => {
        if (productType) {
            const productFields = getPricingFields(productType);
            setFields(productFields);
            // initializeFormData(productFields);
            setInitialized(true);
            if (effectiveProductId) {
                fetchProduct(productFields);
            }
        }
    }, [effectiveProductId, productType, initialized]);

    const fetchProduct = async (defaultFields) => {
        setLoading(true);
        try {
            const response = await fetchProductDetails(effectiveProductId);
            if (response && response.success !== false) {
                setProduct(response);
                // If product has existing calculationInput, load it
                if (response.calculationInput && response.calculationInput.admin_inputs) {
                    initializeFormDataWithExisting(response.calculationInput.admin_inputs);
                } else {
                    initializeFormData(defaultFields)
                }
            } else {
                toast.error('Failed to load product details');
            }
        } catch (error) {
            toast.error('Error loading product');
        } finally {
            setLoading(false);
        }
    };

    const initializeFormData = (fields) => {
        const initialData = {};

        fields.forEach(section => {
            if (section.isRepeater) {
                // Initialize repeater fields as empty array
                if (section.target) {
                    initialData[section.target] = [];
                }
            } else if (section.fields) {
                section.fields.forEach(field => {
                    // Set default values if any
                    if (field.default !== undefined) {
                        initialData[field.name] = field.default;
                    }
                });
            }
        });

        setFormData(initialData);
    };

    const initializeFormDataWithExisting = (existingData) => {
        console.log("Existing Data: ", existingData);
        // Create a copy of existing data
        const newFormData = { ...existingData };

        // Check if we have raw value additions data (prefixed with _)
        if (existingData._valueAdditions && Array.isArray(existingData._valueAdditions)) {
            // Use raw data for value additions
            newFormData.valueAdditions = existingData._valueAdditions;
        } else if (existingData.valueAdditions) {
            // If we only have processed data, keep it as is
            // The form will show placement and pricePerMeter but not the raw materials
            newFormData.accessories = existingData.valueAdditions;
        }

        if (existingData._accessories && Array.isArray(existingData._accessories)) {
            // Use raw data for value additions
            newFormData.accessories = existingData._accessories;
        } else if (existingData.accessories) {
            // If we only have processed data, keep it as is
            // The form will show placement and pricePerMeter but not the raw materials
            newFormData.accessories = existingData.accessories;
        }


        setFormData(newFormData);
    };

    const handleFieldChange = (field, value) => {
        if (field.target) {
            // Handle nested target paths (like "vendorPrices.CLASSIC_WITH_MANUAL.pricePerSqFt")
            const path = field.target.split('.');

            if (path.length === 1) {
                // Simple target
                setFormData(prev => ({
                    ...prev,
                    [field.target]: value
                }));
            } else {
                // Nested target
                setFormData(prev => {
                    const newData = { ...prev };
                    let current = newData;

                    // Navigate to the nested location
                    for (let i = 0; i < path.length - 1; i++) {
                        const key = path[i];
                        if (!current[key] || typeof current[key] !== 'object') {
                            current[key] = {};
                        }
                        current = current[key];
                    }

                    // Set the value at the final key
                    current[path[path.length - 1]] = value;

                    return newData;
                });
            }
        } else {
            // Simple field update
            setFormData(prev => ({
                ...prev,
                [field.name]: value
            }));
        }
    }

    const handleMaterialSelect = (field, material) => {
        console.log("Field in handle Material Select[Pricing Details]: ", field);
        console.log("Material in handle Material Select[Pricing Details]: ", material);

        // Store the material object with underscore prefix
        setFormData(prev => ({
            ...prev,
            [`_${field.name}`]: material
        }));

        // Apply mappings if any
        if (field.mappings && field.mappings.length > 0) {
            const updates = {};

            field.mappings.forEach(mapping => {
                let value;

                // Extract value from material based on mapping.value path
                const valuePath = mapping.value.split('.');

                if (valuePath[0] === 'properties') {
                    value = material.properties?.[valuePath[1]];
                } else if (valuePath[0] === 'specifications') {
                    value = material.specifications?.[valuePath[1]];
                } else {
                    value = material[valuePath[0]];
                }

                // Use default if value not found
                if ((value === undefined || value === null) && mapping.default !== undefined) {
                    value = mapping.default;
                }

                updates[mapping.target] = value;
            });

            setFormData(prev => ({
                ...prev,
                ...updates
            }));
        }
    };

    // Transform formData for API submission
    const prepareForSubmission = () => {
        const adminInputs = {};

        // Copy all root level fields
        Object.keys(formData).forEach(key => {
            // Special handling for valueAdditions
            if (key === 'valueAdditions') {
                // Store raw data with underscore prefix for future editing
                adminInputs[`_${key}`] = formData[key];
            }
            // Special handling for accessories
            else if (key === 'accessories') {
                adminInputs[`_${key}`] = formData[key];
            }
            else {
                adminInputs[key] = formData[key];
            }
        });

        return {
            admin_inputs: adminInputs
        };
    };

    const handleSave = async (action = 'save') => {
        if (!effectiveProductId) {
            toast.error('Please save basic details first');
            return;
        }

        setSaving(true);
        try {
            const submissionData = prepareForSubmission();

            const response = await savePricingDetails(effectiveProductId, submissionData, { action });

            if (response.success) {
                toast.success(response.message || 'Pricing details saved successfully');

                if (action === 'complete') {
                    // Navigate back to product view
                    navigate(`/product/${effectiveProductId}`);
                }
            } else {
                toast.error(response.message || 'Failed to save pricing details');
            }
        } catch (error) {
            console.error('Error saving pricing:', error);
            toast.error('Error saving pricing details');
        } finally {
            setSaving(false);
        }
    };

    if (!selectedCategory && !effectiveProductId) {
        return (
            <div className="container-fluid py-4">
                <div className="card">
                    <div className="card-body text-center py-5">
                        <i className="bi bi-exclamation-circle fs-1 text-muted"></i>
                        <h5 className="mt-3">No Category Selected</h5>
                        <p className="text-muted">Please select a category in the Product Details tab first.</p>
                    </div>
                </div>
            </div>
        );
    }



    if (loading) {
        return <div className="text-center py-5">Loading...</div>;
    }

    return (
        <div className="container-fluid py-4">
            <div className="card">
                <div className="card-body">
                    <h5 className="border-bottom pb-2 mb-4">
                        <i className="bi bi-calculator me-2"></i>
                        Pricing Configuration - {productType}
                        {selectedCategory && (
                            <span className="ms-2 badge bg-secondary">{selectedCategory.name}</span>
                        )}
                    </h5>

                    <DynamicPricingForm
                        fields={fields}
                        values={formData}
                        onChange={handleFieldChange}
                        onMaterialSelect={handleMaterialSelect}
                    />

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => navigate(`/product/${effectiveProductId}`)}
                            disabled={saving}
                        >
                            Cancel
                        </button>

                        <div className="d-flex gap-3">
                            <button
                                className="btn btn-outline-primary"
                                onClick={() => handleSave('save')}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save as Draft'}
                            </button>

                            <button
                                className="btn btn-primary"
                                onClick={() => handleSave('complete')}
                                disabled={saving}
                            >
                                {saving ? 'Processing...' : 'Save & Complete'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PricingDetails;